import { combineReducers, Reducer } from "redux";
import { produce } from "immer";

import { EMPTY_OBJECT } from "@multiweb/reactUtils/constants";
import { ISearchedTextChangedAction, ISetSearchResultsAction } from "../search/types";
import {
    FocusableItemId,
    IClearFocusedItemAction,
    IClearNavigationSectionAction,
    IMenuNavigationState,
    ISetFocusedItemAction,
    ISetNavigationNodesAction,
    MenuNavigationSectionType,
    NavigationNodes,
} from "./types";
import { collapseMenuNavigationSectionType } from "./utils";

export const focusedItemId: Reducer<FocusableItemId | null> = (state = null, action) => {
    if (action instanceof ISetFocusedItemAction) {
        return action.payload.focusedItemId;
    } else if (action instanceof IClearFocusedItemAction) {
        return null;
    } else {
        return state;
    }
};

const INITIAL_NAVIGATION_STATE = {
    [MenuNavigationSectionType.static]: EMPTY_OBJECT,
    [MenuNavigationSectionType.forms]: EMPTY_OBJECT,
    [MenuNavigationSectionType.panelForms]: EMPTY_OBJECT,
    [MenuNavigationSectionType.searchResults]: EMPTY_OBJECT,
    [MenuNavigationSectionType.quickAccess]: EMPTY_OBJECT,
    [MenuNavigationSectionType.notifications]: EMPTY_OBJECT,
    [MenuNavigationSectionType.notificationGroups]: EMPTY_OBJECT,
};

export const navigationNodes: Reducer<NavigationNodes> = (state = INITIAL_NAVIGATION_STATE, action) => {
    if (action instanceof ISetNavigationNodesAction) {
        return produce(state, (draft) => {
            const { section, navigationNodes } = action.payload;
            const collapsedSection = collapseMenuNavigationSectionType(section);
            if (!draft[collapsedSection]) {
                draft[collapsedSection] = {};
            }
            navigationNodes.forEach((navigationNode) => {
                draft[collapsedSection][navigationNode.self.id] = navigationNode;
            });
        });
    } else if (action instanceof IClearNavigationSectionAction) {
        return produce(state, (draft) => {
            draft[collapseMenuNavigationSectionType(action.payload.section)] = EMPTY_OBJECT;
        });
    } else {
        return state;
    }
};

export const firstQuickAccessFocusableItemId: Reducer<FocusableItemId | null> = (state = null, action) => {
    if (action instanceof ISetNavigationNodesAction) {
        const { section, navigationNodes } = action.payload;
        if (section === MenuNavigationSectionType.quickAccess && navigationNodes.length > 0) {
            return navigationNodes[0].self.id;
        }
    } else if (action instanceof IClearNavigationSectionAction) {
        const { section } = action.payload;
        if (section === MenuNavigationSectionType.quickAccess) {
            return null;
        }
    }

    return state;
};

export const lastQuickAccessFocusableItemId: Reducer<FocusableItemId | null> = (state = null, action) => {
    if (action instanceof ISetNavigationNodesAction) {
        const { section, navigationNodes } = action.payload;
        if (section === MenuNavigationSectionType.quickAccess && navigationNodes.length > 0) {
            // poslední položky jsou action itemy, proto se použije rodič získaný přes escId
            return navigationNodes[navigationNodes.length - 1].escId ?? null;
        }
    } else if (action instanceof IClearNavigationSectionAction) {
        const { section } = action.payload;
        if (section === MenuNavigationSectionType.quickAccess) {
            return null;
        }
    }

    return state;
};

export const searchInputSelectAllText: Reducer<boolean> = (state = true, action) => {
    if (action instanceof ISearchedTextChangedAction) {
        return action.payload.searchedText?.length !== 1;
    } else if (action instanceof ISetSearchResultsAction) {
        return true;
    } else {
        return state;
    }
};

export const reducer: Reducer<IMenuNavigationState> = combineReducers({
    focusedItemId,
    firstQuickAccessFocusableItemId,
    lastQuickAccessFocusableItemId,
    searchInputSelectAllText,
    navigationNodes,
});
